import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    useTheme,
    TextField,
    Tooltip, Typography, IconButton
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import PropTypes from "prop-types";
import Spinner from "../../spinner/Spinner";
import {connect} from "react-redux";
import {alertActions} from "../../../store/actions/alertActions";
import {customEventServices} from "../../../service/customEventService";
import {customEventActions} from "../../../store/actions/customEventActions";
import {InfoOutlined} from "@mui/icons-material";
import ReactHookFormChipInput from "../../input/ReactHookFormChipInput";
import {columnServices} from "../../../service/columnService";
import ReactHookFormCheckbox from "../../input/ReactHookFormCheckbox";
import {columnActions} from "../../../store/actions/columnAction";

import {getSettingsColors, settingsInputStyles} from "../SettingsDesign";

export function CustomEventForm(props) {

    const theme = useTheme();
    const {onClose, open, errorMsg, successMsg, clear, rerender, editEventID, setColumns} = props;

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [loading, setLoading] = useState(false);

    const {register, handleSubmit, control, getValues, setValue, formState: {errors}} = useForm();

    const onSubmit = async (data) => {

        setLoading(true);
        clear();

        let ceParamValues = "";
        if (data?.values?.length === 0) {
            errorMsg("Values of The ce Parameter is mandatory field.");
            setLoading(false);
            return false;
        }
        if (data?.values?.length > 0) {
            let valid = true;
            let pattern = /^[a-z-A-Z0-9\s-_]*$/;
            for (const ce of data?.values) {
                if (pattern.test(ce) === false) {
                    errorMsg("Values of The ce Parameter should be a unique value " +
                        "with 100 alphabet characters (a-zA-Z) max and “-”, “_” allowed.");
                    valid = false;
                    break;
                }
                if (ceParamValues === "") {
                    ceParamValues += ce
                } else {
                    ceParamValues += "," + ce
                }
            }
            if (!valid) {
                setLoading(false);
                return false;
            }
        }

        let formData = {
            name: data?.name,
            events: ceParamValues,
            approvedConversionType: data?.approvedConversionType === "" ? false : data?.approvedConversionType,
            includeInConversions: data?.includeInConversions === "" ? false : data?.includeInConversions,
            includeInRevenue: data?.includeInRevenue === "" ? false : data?.includeInRevenue,
            includeInDailyCap: data?.includeInDailyCap === "" ? false : data?.includeInDailyCap,
            sendPostback: data?.sendPostback === "" ? false : data?.sendPostback,
            includePayoutInPostback: data?.includePayoutInPostback === "" ? false : data?.includePayoutInPostback,
            includeInCost: data?.includeInCost === "" ? false : data?.includeInCost,
        };

        if (editEventID > 0) {
            await customEventServices.update(editEventID, formData).then(async () => {
                await columnServices.fetch().then((result) => {
                    setLoading(false);
                    successMsg("Custom event has been successfully updated.");
                    rerender();
                    setColumns(result);
                    handleClose();
                }).catch((e) => {
                    if (e?.response?.data?.error) {
                        errorMsg(e.response.data.error);
                    }
                    setLoading(false);
                });
            }).catch((e) => {
                if (e?.response?.data?.error) {
                    errorMsg(e.response.data.error);
                }
                setLoading(false);
            });
            return false;
        }

        await customEventServices.create(formData).then(async () => {
            await columnServices.fetch().then((result) => {
                setLoading(false);
                successMsg("New custom event has been created successfully.");
                rerender();
                setColumns(result);
                handleClose();
            }).catch((e) => {
                if (e?.response?.data?.error) {
                    errorMsg(e.response.data.error);
                }
                setLoading(false);
            });
        }).catch((e) => {
            if (e?.response?.data?.error) {
                errorMsg(e.response.data.error);
            }
            setLoading(false);
        });
    };

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        setTimeout(() => {
            clear();
        }, 3000);

        onClose();
    };

    useEffect(() => {
        if (editEventID > 0) {
            setLoading(true);
            customEventServices.find(editEventID).then((result) => {
                setValue("name", result?.name);
                setValue("approvedConversionType", result?.approvedConversionType);
                setValue("includeInConversions", result?.includeInConversions);
                setValue("includeInRevenue", result?.includeInRevenue);
                setValue("includeInDailyCap", result?.includeInDailyCap);
                setValue("sendPostback", result?.sendPostback);
                setValue("includePayoutInPostback", result?.includePayoutInPostback);
                setValue("includeInCost", result?.includeInCost);
                setValue("values", result?.events?.split(","));
                setLoading(false);
            }).catch((e) => {
                if (e?.response?.data?.error) {
                    errorMsg(e.response.data.error);
                }
                setLoading(false);
            });
        }
    }, [editEventID, errorMsg, setValue]);

    const reportingOptions = [
        {
            name: 'approvedConversionType', label: 'Use as Approved conversion', defaultValue: true,
            help: 'Count this event in Conversions (Approved) and use it for CR, CV, and AP calculations.'
        },
        {
            name: 'includeInConversions', label: 'Include in Conversions (All) column',
            help: 'Count this event in the total number of conversions.'
        },
        {
            name: 'includeInRevenue', label: 'Include in Revenue column',
            help: 'Include the payout from this event in your revenue.'
        },
        {
            name: 'includeInDailyCap', label: 'Count toward the daily cap',
            help: 'Count this event toward the daily conversion cap configured for an offer.'
        },
        {
            name: 'includeInCost', label: 'Include in Cost',
            help: 'Count this event in cost calculations for CPA and CPS (RevShare) cost models.'
        }
    ];
    const postbackOptions = [
        {
            name: 'sendPostback', label: 'Send postback to traffic source',
            help: 'Send this conversion event to your traffic source via postback.'
        },
        {
            name: 'includePayoutInPostback', label: 'Include payout in postback',
            help: 'Send the event payout amount to your traffic source along with the postback.'
        }
    ];
    const optionGroup = (title, options) => <Box sx={{mt: 3}}>
        <Typography component="h3" sx={{fontSize: 14, fontWeight: 600, mb: 1}}>{title}</Typography>
        <Box sx={{
            p: 1.5, borderRadius: '8px', bgcolor: getSettingsColors(theme).inset,
            '& .MuiFormControlLabel-root': {m: 0, flex: 1, minWidth: 0},
            '& .MuiFormControlLabel-label': {fontSize: 13, whiteSpace: 'normal'}
        }}>
            {options.map(option => <Box key={option.name}
                                        sx={{display: 'flex', alignItems: 'center', gap: 1, minHeight: 40}}>
                <ReactHookFormCheckbox control={control} labelPlacement="end" name={option.name}
                                       defaultValue={option.defaultValue ?? false} label={option.label}/>
                <Tooltip title={option.help}>
                    <IconButton size="small" aria-label={`About ${option.label}`}
                                sx={{color: getSettingsColors(theme).muted}}>
                        <InfoOutlined sx={{fontSize: 18}}/>
                    </IconButton>
                </Tooltip>
            </Box>)}
        </Box>
    </Box>;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth fullScreen={fullScreen}
                aria-labelledby="custom-conversion-form-title" maxWidth="sm"
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: getSettingsColors(theme).card, color: getSettingsColors(theme).text,
                            border: theme.palette.mode === 'dark' ? 1 : 0, borderColor: getSettingsColors(theme).border,
                            borderRadius: fullScreen ? 0 : '16px', ...settingsInputStyles,
                            '& .MuiOutlinedInput-notchedOutline': {border: 0},
                            '& .MuiButton-root': {borderRadius: '8px'},
                            '& .MuiChip-root': {borderRadius: '6px'}
                        }
                    }
                }}>
            <DialogTitle id="custom-conversion-form-title" sx={{m: 0, px: 3, py: 2.5}}>
                <Typography component="span" sx={{display: 'block', fontSize: 22, fontWeight: 700}}>
                    {editEventID > 0 ? 'Edit custom conversion' : 'Create custom conversion'}
                </Typography>
                <Typography component="span" sx={{
                    display: 'block', mt: 0.5, fontSize: 13, fontWeight: 400,
                    color: getSettingsColors(theme).muted, whiteSpace: 'normal'
                }}>
                    Define an event and choose how it appears in your reports.
                </Typography>
            </DialogTitle>
            <DialogContent sx={{p: {xs: 2, sm: 3}, scrollbarGutter: 'stable'}}>
                <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <Box sx={{display: 'grid', gap: 2.5, pt: 2}}>
                        <TextField error={!!errors?.name} type="text" variant="outlined" fullWidth
                                   label="Reporting title" size="small" placeholder="e.g. Purchase"
                                   {...register('name', {required: true, maxLength: 100, minLength: 1, pattern: /^[a-z-A-Z\s-_]*$/})}
                                   slotProps={{inputLabel: {shrink: true}}}/>
                        <ReactHookFormChipInput control={control} name="values" label="Event names"
                                                placeholder="Type an event name and press Enter"
                                                values={getValues('values')}
                                                inputLabelShrink
                                                infoButtonText="Enter one or more names sent through the ce parameter in your postback URL. For example, Purchase or Sale."/>
                    </Box>
                    <Typography sx={{
                        mt: 1.5, fontSize: 12, lineHeight: 1.7, whiteSpace: 'normal',
                        color: getSettingsColors(theme).muted, fontWeight: 400
                    }}>
                        Event names match the <Box component="code"
                                                   sx={{fontFamily: 'monospace', color: getSettingsColors(theme).text}}>ce</Box> parameter
                        in your postback. For example, <Box component="code"
                                                            sx={{fontFamily: 'monospace', color: getSettingsColors(theme).text}}>&amp;ce=Purchase</Box> maps
                        to the reporting title above.
                    </Typography>
                    {optionGroup('Reporting', reportingOptions)}
                    {optionGroup('Traffic source postback', postbackOptions)}
                </form>
            </DialogContent>
            <DialogActions sx={{m: 0, px: 3, py: 2}}>
                <Button onClick={handleClose} color="inherit" variant="text">Cancel</Button>
                {loading ? <Spinner/> :
                    <Button onClick={handleSubmit(onSubmit)} color="primary" variant="contained">Save & Close</Button>}
            </DialogActions>
        </Dialog>
    );

}

CustomEventForm.prototype = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    editEventID: PropTypes.number
};

const mapStateToProps = (state) => {
        return {
            state: {
                alert: state.alert,
            }
        };
    }
;

const mapDispatchToProps = {
    errorMsg: alertActions.error,
    successMsg: alertActions.success,
    clear: alertActions.clear,
    rerender: customEventActions.rerender,
    setColumns: columnActions.setColumns
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomEventForm);